import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, Inject, OnInit, OnDestroy, Renderer2 } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SlidePanelService, SlidePanelState, SlidePanelTab } from 'src/app/services/slide-panel.service';
import { JobProfileDetailComponent } from 'src/app/components/job-module/job-profile-detail/job-profile-detail.component';
import { CandidateProfileSummaryComponent } from 'src/app/components/candidate-module/candidate-profile-summary/candidate-profile-summary.component';
import { CandidateProfileSliderComponent } from 'src/app/components/slider-profile/candidate-profile-slider/candidate-profile-slider.component';
import { JobProfileSliderComponent } from 'src/app/components/slider-profile/job-profile-slider/job-profile-slider.component';

@Component({
  selector: 'app-slide-panel',
  standalone: true,
  imports: [
    CommonModule,
    JobProfileDetailComponent,
    CandidateProfileSummaryComponent,
    CandidateProfileSliderComponent,
    JobProfileSliderComponent
  ],
  templateUrl: './slide-panel.component.html',
  styleUrls: ['./slide-panel.component.scss']
})
export class SlidePanelComponent implements OnInit, OnDestroy {
  isOpen = false;
  panelType: 'job' | 'candidate' | null = null;
  panelData: any = null;
  candidateId: string | undefined = undefined;
  jobId: string | undefined = undefined;
  /** Job context for the candidate slider's Pay Rate range. */
  jobData: any = null;
  /** Candidate context for the job slider's "Submitted for" rate. */
  candidateData: any = null;
  // V3 extensions — drive the new 4-tab sliders vs. the legacy V2 summary.
  variant: 'v2' | 'v3' = 'v2';
  initialTab: SlidePanelTab | undefined = undefined;
  pageFrom: string | undefined = undefined;
  hideAiInsights = false;
  hideChat = false;
  // Single-scroll drawer — only the SuperAdmin org pages set this true.
  // Drives the .v3-single-scroll class on the drawer host.
  compactScroll = false;

  private subscription: Subscription;
  // Router subscription — closes the drawer whenever the app navigates away
  // (session expiry → logout redirect, manual logout, or any route change),
  // so the overlay can never linger on top of a different page.
  private routerSubscription: Subscription;

  // Class toggled on <body> while the drawer is open. The matching global rule
  // in styles.scss locks document scroll AND suppresses scrollbars on the
  // pages whose primary content sits in their own overflow:auto wrapper
  // (Job Center / Talent Center / Talent Pool list wrappers, V3 entity body),
  // so users no longer see two side-by-side scrollbars when the drawer is up.
  private static readonly BODY_OPEN_CLASS = 'slide-panel-open';

  constructor(
    private slidePanelService: SlidePanelService,
    private renderer: Renderer2,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) { }

  ngOnInit(): void {
    this.subscription = this.slidePanelService.panelState$.subscribe(
      (state: SlidePanelState) => {
        const wasOpen = this.isOpen;
        if (state.isOpen && !wasOpen) {
          this.armBackdropGuard();
        } else if (!state.isOpen) {
          clearTimeout(this.backdropGuardTimer);
          this.backdropGuardTimer = null;
        }
        this.isOpen = state.isOpen;
        this.panelType = state.type;
        this.panelData = state.data;
        this.candidateId = state.candidateId;
        this.jobId = state.jobId;
        this.jobData = state.jobData ?? null;
        this.candidateData = state.candidateData ?? null;
        this.variant = state.variant ?? 'v2';
        this.initialTab = state.initialTab;
        this.pageFrom = state.pageFrom;
        this.hideAiInsights = state.hideAiInsights ?? false;
        this.hideChat = state.hideChat ?? false;
        this.compactScroll = state.compactScroll ?? false;
        this.toggleBodyScrollLock(this.isOpen);
      }
    );

    // On any navigation, close an open drawer before the new page renders.
    // Guarded by `isOpen` so we don't emit redundant close states on every
    // route change. Covers session-expiry logout (interceptor redirect) as
    // well as manual logout and normal in-app navigation.
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => {
        if (this.isOpen) {
          this.slidePanelService.close();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    clearTimeout(this.backdropGuardTimer);
    // Safety net — ensure the lock is released if the component is destroyed
    // while the drawer was still considered open.
    this.toggleBodyScrollLock(false);
  }

  private toggleBodyScrollLock(lock: boolean): void {
    const body = this.document?.body;
    // Lock the <html> element too — pages that scroll the window
    // (e.g. Job Center / Talent Center list wrappers use
    // [scrollWindow]="true") put the scrollbar on documentElement, which
    // overflow:hidden on <body> alone doesn't suppress.
    const html = this.document?.documentElement;
    if (lock) {
      if (body) this.renderer.addClass(body, SlidePanelComponent.BODY_OPEN_CLASS);
      if (html) this.renderer.addClass(html, SlidePanelComponent.BODY_OPEN_CLASS);
    } else {
      if (body) this.renderer.removeClass(body, SlidePanelComponent.BODY_OPEN_CLASS);
      if (html) this.renderer.removeClass(html, SlidePanelComponent.BODY_OPEN_CLASS);
    }
  }

  close(): void {
    this.slidePanelService.close();
  }

  /** How long after the drawer opens the backdrop refuses to dismiss it. */
  private static readonly BACKDROP_GUARD_MS = 500;
  private backdropGuardTimer: any = null;

  private armBackdropGuard(): void {
    clearTimeout(this.backdropGuardTimer);
    this.backdropGuardTimer = setTimeout(
      () => { this.backdropGuardTimer = null; },
      SlidePanelComponent.BACKDROP_GUARD_MS
    );
  }

  onBackdropClick(): void {
    // Opening the drawer flips this backdrop to display:block across the whole
    // viewport — directly over the row title that was just clicked. So the
    // next click of a burst lands here and dismisses the drawer, the one after
    // that hits the title again and reopens it, and the drawer visibly
    // flickers open/shut. Confirmed from a console trace: every close came
    // from this handler, never from the X or a navigation.
    //
    // Swallow backdrop clicks that belong to the burst that opened the drawer.
    // Each swallowed click pushes the window out, so holding the mouse down or
    // hammering the row can never dismiss it. The timer always expires, so a
    // deliberate click-away after a pause closes the drawer exactly as before.
    if (this.backdropGuardTimer !== null) {
      this.armBackdropGuard();
      return;
    }
    this.close();
  }
}
